import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  TuiDialogOptions,
  TuiDialogService,
  TuiNotificationService,
} from '@taiga-ui/core';
import { mergeMap, Observable, Subject } from 'rxjs';
import { TUI_CONFIRM, TuiConfirmData } from '@taiga-ui/kit';
import {
  XAlertServiceTranslations,
  XAlertServiceTranslationsPayload,
} from '../types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** `false` — не закрывать автоматически (в Taiga v5 это `0`). */
function toAutoClose(autoClose: number | boolean): number {
  if (autoClose === false) {
    return 0;
  }

  return autoClose === true ? 3000 : autoClose;
}

@Injectable({
  providedIn: 'root',
})
export class XAlertService {
  private readonly alertService$ = inject(TuiNotificationService);
  private readonly dialogs: TuiDialogService = inject(TuiDialogService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private translates: XAlertServiceTranslationsPayload = {
    [XAlertServiceTranslations.Yes]: 'Да',
    [XAlertServiceTranslations.No]: 'Нет',
  };

  private showSimpleMessage$ = new Subject<
    Parameters<typeof this.alertService$.open>
  >();

  constructor() {
    this.init$().subscribe();
  }

  init$(): Observable<unknown> {
    this.outkeepTranslateService
      .translate([XAlertServiceTranslations.Yes, XAlertServiceTranslations.No])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (translates: XAlertServiceTranslationsPayload) =>
          (this.translates = translates),
      );

    // Строку передаём как есть: Taiga 5 сама выводит её через [innerHTML] (с санитайзером Angular,
    // безопасные теги вроде <b>/<strong>/<br> сохраняются). SafeHtml-объект PolymorpheusOutlet
    // не умеет отрисовать — уведомление получалось пустым.
    return this.showSimpleMessage$.pipe(
      mergeMap(([content, options]) =>
        this.alertService$.open(content, options),
      ),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  showMessage(...params: Parameters<typeof this.alertService$.open>): void {
    this.showSimpleMessage$.next(params);
  }

  success(
    title: string,
    msg?: string,
    autoClose: number | boolean = 3000,
  ): void {
    this.showMessage(msg ? `<strong>${msg}</strong>` : '', {
      label: title,
      autoClose: toAutoClose(autoClose),
      appearance: 'positive',
    });
  }

  info(title: string, msg?: string, autoClose: number | boolean = 3000): void {
    this.showMessage(msg ? `<strong>${msg}</strong>` : '', {
      label: title,
      autoClose: toAutoClose(autoClose),
      appearance: 'info',
    });
  }

  warn(title: string, msg?: string, autoClose: number | boolean = 3000): void {
    this.showMessage(msg ? `<strong>${msg}</strong>` : '', {
      label: title,
      autoClose: toAutoClose(autoClose),
      appearance: 'warning',
    });
  }

  error(title: string, msg?: string, autoClose: number | boolean = 3000): void {
    this.showMessage(msg ? `<strong>${msg}</strong>` : '', {
      label: title,
      autoClose: toAutoClose(autoClose),
      appearance: 'negative',
    });
  }

  showDialog(
    title: string,
    msg: string,
    yes = this.translates[XAlertServiceTranslations.Yes],
    no = this.translates[XAlertServiceTranslations.No],
    size: TuiDialogOptions<TuiConfirmData>['size'] = 's',
  ): Observable<boolean> {
    const data: TuiConfirmData = {
      content: msg,
      yes,
      no,
    };

    return this.dialogs.open<boolean>(TUI_CONFIRM, {
      label: title,
      size,
      data,
    });
  }
}
